import { useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsCheckLg } from "react-icons/bs";
import { HiArrowLeft } from "react-icons/hi";
import { Link, useNavigate } from "react-router-dom";
import { Button, Select } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import {
  useDebouncedValue,
  useDocumentTitle,
  useLocalStorage,
  useSessionStorage,
} from "@mantine/hooks";
import { IconCalendar } from "@tabler/icons-react";

import { WithSidebar } from "@layouts/WithSidebar";

import { CustomPhoneInput } from "@components/CustomPhoneInput";

import { calculateBMI } from "@utils/calculateBMI";
import { formatDateToString } from "@utils/dateValidation";
import { handleOnChange } from "@utils/handleOnChange";
import {
  showErrorNotification,
  showInfoNotification,
} from "@utils/showNotification";

import { getCreatePatientSteps } from "@data/createPatientSteps";
import {
  bloodTypeValues,
  getDietValues,
  getGenderValues,
  getSmokingHabitsValues,
  getYesNoValues,
  insuranceTypeValues,
} from "@data/dropdownValues";

import { LARUCHE_IDENTIFIER, PAGE_TITLE } from "@constants/index";

import { useCreatePatient } from "@hooks/mutations/patient/useCreatePatient";
import { useGetPatientByPhoneNumber } from "@hooks/queries/patient/useGetPatientByPhoneNumber";

import { Patient } from "@customTypes/patient";

import { StyledCreatePatient } from "./CreatePatient.styled";


const TOTAL_STEPS = getCreatePatientSteps().length;

export const CreatePatient = () => {
  const { t } = useTranslation();

  useDocumentTitle(`${t("create-patient")} • ${PAGE_TITLE}`);
  const navigate = useNavigate();
  const [sessionStep, setSessionStep] = useSessionStorage({
    key: "cp-step",
    defaultValue: "0", //* Avoiding using 1 because there is an undesirable flash of the first step when the page loads
  });
  const [localForm, setLocalForm] = useLocalStorage({
    key: `${LARUCHE_IDENTIFIER}-cp-form`,
    defaultValue: "{}",
  });

  const [step, setStep] = useState(+sessionStep);
  const [moveToStep, setMoveToStep] = useState(0);
  const [gender, setGender] = useState<string | null>(null);
  const [dob, setDob] = useState<Date | null>(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [altPhoneNumber, setAltPhoneNumber] = useState("");
  const [emergencyPhoneNumber, setEmergencyPhoneNumber] = useState("");
  const [isDonor, setIsDonor] = useState<string | null>(null);
  const [bloodType, setBloodType] = useState<string | null>(null);
  const [diet, setDiet] = useState<string | null>(null);
  const [smokingHabits, setSmokingHabits] = useState<string | null>(null);
  const [insuranceType, setInsuranceType] = useState<string | null>(null);
  const [insuranceExpiryDate, setInsuranceExpiryDate] = useState<Date | null>(
    null
  );
  const [isPhoneInputError, setIsPhoneInputError] = useState(false);
  const [isAltPhoneInputError, setIsAltPhoneInputError] = useState(false);
  const [isEmergencyPhoneInputError, setIsEmergencyPhoneInputError] =
    useState(false);
  const [formData, setFormData] = useState({} as Patient);
  const [debouncedForm] = useDebouncedValue(
    {
      ...formData,
      ...(dob && { dob }),
      ...(insuranceExpiryDate && { insuranceExpiry: insuranceExpiryDate }),
    },
    1000 * 2
  );
  const [isMounted, setIsMounted] = useState(true);

  const { mutate: createPatient, isLoading: isCreatePatientLoading } =
    useCreatePatient();

  const { isSuccess: isExistingPhoneNumber } = useGetPatientByPhoneNumber({
    phoneNumber,
    isEnabled: phoneNumber.length >= 10,
  });

  const handleFormSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (step < TOTAL_STEPS) {
      if (moveToStep) {
        setStep(moveToStep);
        setSessionStep(String(moveToStep));
        setMoveToStep(0);
      } else {
        setStep(step + 1);
        setSessionStep(String(step + 1));
      }
    } else if (step === TOTAL_STEPS) {
      const newForm = {
        ...formData,
        dob: formatDateToString(dob as Date),
        insuranceExpiryDate: insuranceExpiryDate
          ? formatDateToString(insuranceExpiryDate)
          : "",
      };
      console.log("Submit create patient form:", newForm);
      createPatient(newForm, {
        onSuccess: (res: any) => {
          setFormData({} as Patient);
          setLocalForm("{}");
          setStep(1);
          setSessionStep(String(1));
          navigate(`/patients/${res.data.patient_id}/profile`);
        },
      });
    }
  };

  useEffect(() => {
    if (gender) {
      setFormData(prevData => ({
        ...prevData,
        gender,
      }));
    }
  }, [gender]);

  useEffect(() => {
    if (phoneNumber) {
      setFormData(prevData => ({
        ...prevData,
        phoneNumber,
      }));
    }
  }, [phoneNumber]);

  useEffect(() => {
    if (altPhoneNumber) {
      setFormData(prevData => ({
        ...prevData,
        altPhoneNumber,
      }));
    }
  }, [altPhoneNumber]);

  useEffect(() => {
    if (emergencyPhoneNumber) {
      setFormData(prevData => ({
        ...prevData,
        emergencyContactPhoneNumber: emergencyPhoneNumber,
      }));
    }
  }, [emergencyPhoneNumber]);

  useEffect(() => {
    if (isDonor) {
      setFormData(prevData => ({
        ...prevData,
        bloodDonor: isDonor,
      }));
    }
  }, [isDonor]);

  useEffect(() => {
    if (bloodType) {
      setFormData(prevData => ({
        ...prevData,
        bloodType,
      }));
    }
  }, [bloodType]);

  useEffect(() => {
    if (diet) {
      setFormData(prevData => ({
        ...prevData,
        diet,
      }));
    }
  }, [diet]);

  useEffect(() => {
    if (smokingHabits) {
      setFormData(prevData => ({
        ...prevData,
        smokingHabits,
      }));
    }
  }, [smokingHabits]);

  useEffect(() => {
    if (insuranceType) {
      setFormData(prevData => ({
        ...prevData,
        insuranceType,
      }));
    }
  }, [insuranceType]);

  // useEffect(() => {
  //   console.log("[Patient form data]", formData);
  // }, [formData]);

  useLayoutEffect(() => {
    if (
      +sessionStep > 1 &&
      (Object.keys(formData).length ||
        Object.keys(JSON.parse(localForm)).length)
    ) {
      setStep(+sessionStep);
    } else {
      setStep(1);
    }
  }, [sessionStep]);

  useEffect(() => {
    if (moveToStep) {
      if (
        !formData?.firstName ||
        !formData?.surname ||
        !dob ||
        !phoneNumber ||
        !gender ||
        isPhoneInputError
      ) {
        showErrorNotification({ message: "required-fields-error" });
      } else {
        setStep(moveToStep);
        setSessionStep(String(moveToStep));
        setMoveToStep(0);
      }
    }
  }, [moveToStep]);

  useEffect(() => {
    const parsedSessionForm = JSON.parse(localForm) as Patient;
    if (
      isMounted &&
      !Object.keys(formData).length &&
      Object.keys(parsedSessionForm).length
    ) {
      showInfoNotification({
        title: t("previous-data-title"),
        message: t("previous-data-message"),
      });
      setFormData(parsedSessionForm);

      if (parsedSessionForm.dob) {
        setDob(new Date(parsedSessionForm.dob));
      }
      if (parsedSessionForm.insuranceExpiry) {
        setInsuranceExpiryDate(
          new Date(parsedSessionForm.insuranceExpiry as string)
        );
      }
      if (parsedSessionForm.phoneNumber) {
        setPhoneNumber(parsedSessionForm.phoneNumber);
      }
      if (parsedSessionForm.altPhoneNumber) {
        setAltPhoneNumber(parsedSessionForm.altPhoneNumber);
      }
      if (parsedSessionForm.emergencyContactPhoneNumber) {
        setAltPhoneNumber(parsedSessionForm.emergencyContactPhoneNumber);
      }
      if (parsedSessionForm.gender) {
        setGender(parsedSessionForm.gender);
      }

      setIsMounted(false);
    }
  }, [localForm]);

  useEffect(() => {
    if (Object.keys(debouncedForm).length) {
      setLocalForm(JSON.stringify(debouncedForm));
    }
  }, [debouncedForm]);

  return (
    <WithSidebar>
      <StyledCreatePatient>
        <div className="header">
          <div className="back-btn-wrapper">
            <Link to=".." className="back-btn">
              <HiArrowLeft /> <span>{t("back")}</span>
            </Link>
            <h2>{t("create-patient")}</h2>
          </div>
        </div>

        <div className="body">
          <ul className="stepper">
            {getCreatePatientSteps().map(({ title, position }) => (
              <li key={position} className={`stepper-item`}>
                <button
                  className={` ${step === position ? "active" : ""} ${
                    step > position ? "completed" : ""
                  }`}
                  onClick={() => {
                    setMoveToStep(position);
                  }}>
                  <div className="icon-wrapper">
                    {step <= position ? <span>{position}</span> : <BsCheckLg />}
                  </div>
                  <span>{title}</span>
                </button>
              </li>
            ))}
          </ul>

          <form className="form" onSubmit={handleFormSubmit}>
            {step === 1 ? (
              <div className="form-step">
                <div className="form-group">
                  <div className="form-field">
                    <label htmlFor="firstName">
                      {t("first-name")}
                      <sup>*</sup>
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      onChange={e => handleOnChange(e, setFormData, formData)}
                      value={formData?.firstName ?? ""}
                      placeholder={t("eg", { example: "John" })}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="surname">
                      {t("surname")}
                      <sup>*</sup>
                    </label>
                    <input
                      type="text"
                      id="surname"
                      name="surname"
                      onChange={e => handleOnChange(e, setFormData, formData)}
                      value={formData?.surname ?? ""}
                      placeholder={t("eg", { example: "Doe" })}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <div className="form-field">
                    <label htmlFor="middleName">{t("middle-name")}</label>
                    <input
                      type="text"
                      id="middleName"
                      name="middleName"
                      onChange={e => handleOnChange(e, setFormData, formData)}
                      value={formData?.middleName ?? ""}
                      placeholder={t("eg", { example: "Rick" })}
                    />
                  </div>
                  <div className="form-field">
                    <DatePickerInput
                      label={t("dob")}
                      value={dob}
                      onChange={setDob}
                      placeholder={t("select-date")}
                      withAsterisk
                      leftSection={<IconCalendar size={18} stroke={1.5} />}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <div className="form-field">
                    <label htmlFor="phone">
                      {t("phone-number")}
                      <sup>*</sup>
                    </label>
                    <CustomPhoneInput
                      id="phone"
                      phone={phoneNumber}
                      setPhone={setPhoneNumber}
                      required
                      setIsError={setIsPhoneInputError}
                      isExistingNumber={isExistingPhoneNumber}
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="altPhoneNumber">
                      {t("alt-phone-number")}
                    </label>
                    <CustomPhoneInput
                      id="altPhoneNumber"
                      phone={altPhoneNumber || ""}
                      setPhone={setAltPhoneNumber}
                      setIsError={setIsAltPhoneInputError}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <div className="form-field">
                    <Select
                      label={t("gender")}
                      placeholder={t("select-gender")}
                      data={getGenderValues()}
                      value={gender}
                      onChange={setGender}
                      withAsterisk
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="email">{t("email")}</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      onChange={e => handleOnChange(e, setFormData, formData)}
                      value={formData?.email ?? ""}
                      placeholder={t("eg", { example: "johndoe@gmail.com" })}
                    />
                  </div>
                </div>

                {/* <div className="form-field half">
                  <label htmlFor="occupation">Occupation</label>
                  <input
                    type="text"
                    placeholder="Eg: Software Engineer"
                    id="occupation"
                  />
                </div> */}
              </div>
            ) : null}

            {step === 2 ? (
              <div className="form-step">
                <fieldset>
                  <legend>{t("emergency-contact")}</legend>

                  <div className="form-group">
                    <div className="form-field">
                      <label htmlFor="emergencyContactName">{t("name")}</label>
                      <input
                        type="text"
                        placeholder={t("eg", { example: "John Doe" })}
                        id="emergencyContactName"
                        name="emergencyContactName"
                        onChange={e => handleOnChange(e, setFormData, formData)}
                        value={formData?.emergencyContactName ?? ""}
                      />
                    </div>
                    <div className="form-field">
                      <label htmlFor="emergencyContactPhoneNumber">
                        {t("phone-number")}
                      </label>
                      <CustomPhoneInput
                        id="emergencyContactPhoneNumber"
                        phone={formData?.emergencyContactPhoneNumber ?? ""}
                        setPhone={setEmergencyPhoneNumber}
                        setIsError={setIsEmergencyPhoneInputError}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="form-field">
                      <label htmlFor="emergencyContactRelationship">
                        {t("relationship")}
                      </label>
                      <input
                        type="text"
                        placeholder={t("eg", { example: t("father") })}
                        id="emergencyContactRelationship"
                        name="emergencyContactRelationship"
                        onChange={e => handleOnChange(e, setFormData, formData)}
                        value={formData?.emergencyContactRelationship ?? ""}
                      />
                    </div>
                    <div className="form-field">
                      <label htmlFor="emergencyContactCountry">
                        {t("country")}
                      </label>
                      <input
                        type="text"
                        id="emergencyContactCountry"
                        name="emergencyContactCountry"
                        placeholder={t("eg", { example: "Côte d'Ivoire" })}
                        onChange={e => handleOnChange(e, setFormData, formData)}
                        value={formData?.emergencyContactCountry ?? ""}
                      />
                    </div>
                  </div>
                </fieldset>
              </div>
            ) : null}

            {step === 3 ? (
              <div className="form-step">
                <div className="form-group">
                  <div className="form-field">
                    <label htmlFor="exerciseFrequency">
                      {t("exercise-frequency")}
                    </label>
                    <input
                      type="text"
                      id="exerciseFrequency"
                      name="exerciseFrequency"
                      onChange={e => handleOnChange(e, setFormData, formData)}
                      value={formData?.exerciseFrequency ?? ""}
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="alcohol">{t("alcohol-consumption")}</label>
                    <input
                      type="text"
                      id="alcohol"
                      name="drinkingHabits"
                      onChange={e => handleOnChange(e, setFormData, formData)}
                      value={formData?.drinkingHabits ?? ""}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <div className="form-field">
                    <Select
                      label={t("diet")}
                      placeholder={t("select-option")}
                      data={getDietValues()}
                      value={formData?.diet}
                      onChange={setDiet}
                    />
                  </div>
                  <div className="form-field">
                    <Select
                      label={t("smoking-habits")}
                      placeholder={t("select-option")}
                      data={getSmokingHabitsValues()}
                      value={formData?.smokingHabits}
                      onChange={setSmokingHabits}
                    />
                  </div>
                </div>

                <div className="form-field half">
                  <label htmlFor="occupation">{t("occupation")}</label>
                  <input
                    type="text"
                    id="occupation"
                    placeholder={t("eg", { example: t("doctor") })}
                    name="occupation"
                    onChange={e => handleOnChange(e, setFormData, formData)}
                    value={formData?.occupation ?? ""}
                  />
                </div>
              </div>
            ) : null}

            {step === 4 ? (
              <div className="form-step">
                <div className="form-group">
                  <div className="form-field">
                    <Select
                      label={t("blood-type")}
                      placeholder={t("select-blood-type")}
                      data={bloodTypeValues}
                      value={formData?.bloodType}
                      onChange={setBloodType}
                    />
                  </div>
                  <div className="form-field">
                    <Select
                      label={t("is-patient-donor")}
                      placeholder={t("select-option")}
                      data={getYesNoValues()}
                      value={formData?.bloodDonor}
                      onChange={setIsDonor}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <div className="form-field">
                    <label htmlFor="height">{t("height")}</label>
                    <input
                      type="number"
                      id="height"
                      placeholder={t("eg", { example: "175" })}
                      onChange={e => handleOnChange(e, setFormData, formData)}
                      value={formData?.height ?? ""}
                      name="height"
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="weight">{t("weight")}</label>
                    <input
                      type="number"
                      id="weight"
                      placeholder={t("eg", { example: "75" })}
                      onChange={e => handleOnChange(e, setFormData, formData)}
                      value={formData?.weight ?? ""}
                      name="weight"
                    />
                  </div>
                </div>

                <div className="form-field half">
                  <label htmlFor="bmi">{t("bmi")}</label>
                  <input
                    type="text"
                    id="bmi"
                    value={calculateBMI(
                      formData?.height as string,
                      formData?.weight as string
                    )}
                    readOnly
                  />
                </div>
              </div>
            ) : null}

            {/* {step === 5 ? (
              <div className="form-step">
                <div className="form-group">
                  <SurgicalHistoryCard surgeries={[]} />
                  <MedicalHistoryCard medicalHistory={[]} />
                </div>
                <div className="form-group" style={{ marginTop: "1.5rem" }}>
                  <AllergyCard />
                </div>
              </div>
            ) : null} */}

            {step === 5 ? (
              <div className="form-step">
                <div className="form-group">
                  <div className="form-field">
                    <Select
                      label={t("payer")}
                      data={insuranceTypeValues}
                      placeholder={t("select-option")}
                      value={formData?.insuranceType}
                      onChange={setInsuranceType}
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="insurancePolicyNumber">
                      {t("policy-number")}
                    </label>
                    <input
                      type="text"
                      id="insurancePolicyNumber"
                      name="insurancePolicyNumber"
                      onChange={e => handleOnChange(e, setFormData, formData)}
                      value={formData?.insurancePolicyNumber ?? ""}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <div className="form-field">
                    <label htmlFor="insuranceProvider">{t("provider")}</label>
                    <input
                      type="text"
                      id="insuranceProvider"
                      name="insuranceProvider"
                      onChange={e => handleOnChange(e, setFormData, formData)}
                      value={formData?.insuranceProvider ?? ""}
                    />
                  </div>
                  <div className="form-field">
                    <DatePickerInput
                      label={t("expiry-date")}
                      value={insuranceExpiryDate}
                      onChange={setInsuranceExpiryDate}
                      placeholder={t("select-date")}
                      leftSection={<IconCalendar size={18} stroke={1.5} />}
                    />
                  </div>
                </div>
              </div>
            ) : null}

            <div className="footer">
              {/* <Button
                variant="transparent"
                size="compact-sm"
                disabled={
                  step === TOTAL_STEPS ||
                  isCreatePatientLoading ||
                  isPhoneInputError ||
                  !formData?.phoneNumber ||
                  formData?.phoneNumber.length !== 13 //* Will need better validation for different countries' phone numbers later
                }
                onClick={e => {
                  e.preventDefault();
                  if (step === TOTAL_STEPS) return;
                  setStep(TOTAL_STEPS);
                  setSessionStep(String(TOTAL_STEPS));
                }}>
                {t("skip-to-end")}
              </Button> */}
              <Button
                variant="transparent"
                size="compact-sm"
                disabled={isCreatePatientLoading}
                onClick={e => {
                  e.preventDefault();
                  setFormData({} as Patient);
                  setDob(null);
                  setInsuranceExpiryDate(null);
                  setPhoneNumber("");
                  setAltPhoneNumber("");
                  setEmergencyPhoneNumber("");
                  setLocalForm("{}");
                  setMoveToStep(0);
                  setStep(1);
                  return;
                }}>
                {t("reset")}
              </Button>
              <div className="wrapper">
                {step > 1 ? (
                  <Button
                    variant="outline"
                    disabled={
                      isCreatePatientLoading ||
                      isPhoneInputError ||
                      isEmergencyPhoneInputError ||
                      isAltPhoneInputError
                    }
                    onClick={e => {
                      e.preventDefault();
                      if (isPhoneInputError) return;
                      if (step === 1) return;
                      setStep(step - 1);
                      setSessionStep(String(step - 1));
                    }}>
                    {t("previous")}
                  </Button>
                ) : null}
                <Button
                  type="submit"
                  disabled={
                    isPhoneInputError ||
                    isEmergencyPhoneInputError ||
                    isAltPhoneInputError ||
                    !formData?.phoneNumber ||
                    formData?.phoneNumber.length !== 13 //* Will need better validation for different countries' phone numbers later
                  }
                  loading={isCreatePatientLoading}>
                  {step === TOTAL_STEPS ? t("submit") : t("next")}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </StyledCreatePatient>
    </WithSidebar>
  );
};
